import { darken } from 'polished';
import React, { useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';

import FlexContainer from 'components/lib/ui/FlexContainer';
import Icon from 'components/lib/ui/Icon';
import Text from 'components/lib/ui/Text';

import { fontSize, fontWeight, radius, spacing, variables } from 'common/lib/theme/dynamic';
import { useActiveCommandView } from 'lib/commands/context';
import useKey from 'lib/hooks/useKey';

/**
 * Component that renders the UI for a command view
 */
export const CommandView: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const { activeCommandView, closeCommandView } = useActiveCommandView();
  const { component: CommandComponent, label, hideLabel } = activeCommandView ?? {};

  useKey('escape', ref.current, {
    onKeyDown: closeCommandView,
  });

  useLayoutEffect(() => {
    ref.current?.focus();
  }, []);

  // If there's no active command view, don't render anything
  if (!activeCommandView || !CommandComponent) {
    return null;
  }

  return (
    <Root ref={ref}>
      <Header>
        <BackButton onClick={closeCommandView}>
          <Icon name="arrow-left" size={12} />
          Back
        </BackButton>
        {!hideLabel && (
          <Text size="small" weight="medium">
            {label}
          </Text>
        )}
      </Header>

      <ContentContainer>
        <CommandComponent close={closeCommandView} />
      </ContentContainer>
    </Root>
  );
};

const Root = styled(FlexContainer).attrs({
  tabIndex: -1,
  column: true,
})`
  outline: none;
`;

const Header = styled.div`
  padding: ${spacing.xsmall};
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;
  justify-content: space-between;
`;

const BackButton = styled.button`
  --color: ${variables.color.content.secondary};
  --hover-color: ${({ theme }) => darken(0.1, theme.variables.color.content.secondary)};

  background: none;
  border: none;
  cursor: pointer;
  color: var(--color);
  display: flex;
  align-items: center;
  gap: ${spacing.xxsmall};
  font-size: ${fontSize.small};
  font-weight: ${fontWeight.medium};
  padding: ${spacing.xsmall};
  border-radius: ${radius.small};
  width: fit-content;

  &:hover {
    color: var(--hover-color);
  }
`;

const ContentContainer = styled.div`
  flex: 1;
  overflow-y: auto;
`;
